/**
 * Zustand Store for Summarizer State Management
 * Merged with App state - handles all summarization state and API interactions
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { APIResponse, Summary } from '../../types/api';
import { SummarizerConfig } from '../../types/summarizer';
import { SOURCE_TYPES } from '../../constants/summarizer';
import { PromptLibraryItem, PromptLibraryResponse } from '../../config/prompt-library';
import axios from 'axios';

// Import new API types
import { UserQuotaData, SummarizeRequest } from '../../types/api';
// import { tempYoutubeSummary } from './temp-data';

interface SummarizeRequestData {
  source: string;
  link_or_id?: string;
  text?: string;
  prompt_type?: string;
  title?: string;
  extension?: string;
  summaryType?: string;
  tone?: string;
  format?: string;
  language?: string;
  custom_prompt?: string;
}

// Summary Cache Types
interface SummaryCacheEntry {
  content: string;
  timestamp: number;
  detail_level: string;
  tone: string;
  language: string;
  custom_prompt?: string;
}

interface SummarizerState {
  // UI State
  sidebarOpen: boolean;
  theme: 'light' | 'dark' | 'auto';
  language: string;

  // Input State
  urlInput: string;
  textInput: string;
  selectedFile: File | null;

  // Validation State
  urlError: string | null;
  fileError: string | null;
  textError: string | null;

  // Summarizer State
  currentType: 'url' | 'file' | 'text';
  activeTab: 'content' | 'repurpose' | 'chat' | 'transcript';
  isLoading: boolean;
  error: string | null;

  // Results
  currentSummary: Summary | null;
  summaryHistory: Summary[];

  // Summary Cache - stores multiple variants by detail_level + tone + custom_prompt
  summaryCache: Record<string, SummaryCacheEntry>;

  // Configuration Management
  currentConfig: SummarizerConfig | null;

  // Translation State - Enhanced with detail_level and tone
  translations: Record<
    string, // Key: language_detailLevel_tone
    {
      summary: string | null;
      summary_as_md: string | null;
      summary_html: string | null;
      tldr?: string;
      detail_level: string;
      tone: string;
      language: string;
      timestamp: number;
    }
  >;
  isTranslating: boolean;
  translationError: string | null;
  failedTranslations: Set<string>; // Track failed translation attempts (key format: lang_detail_tone)

  // File upload
  uploadProgress: number;
  isUploading: boolean;
  uploadedUrl: string;
  uploadedFile: File | null;

  // API Configuration - Updated for new API structure
  backendBaseUrl: string;
  webAppBaseUrl: string;
  apiBaseUrl: string; // New: Single base URL for new API

  // UI Options
  summaryType: string;
  tone: string;
  format: string;
  promptType: string;
  customPrompt: string | null;

  // Limit/Error State
  limitOpen: boolean;

  // Repurpose Content State
  isRepurposing: boolean;
  repurposeError: string | null;
  selectedLanguage: string;
  prompts: PromptLibraryItem[];
  categories: string[];
  isLoadingPromptLibrary: boolean;
  promptLibraryError: string | null;
  hasAttemptedPromptFetch: boolean;
  showCustomPromptModal: boolean;
  activePrompt: PromptLibraryItem | null;

  // New: Quota Management State
  quota: UserQuotaData | null;
  quotaLoading: boolean;
  quotaError: string | null;

  // CTA Dialog State
  showCTADialog: boolean;
  ctaDialogType: string;
  ctaErrorMessage: string | null;
  ctaFeatureContext: string | null;
}

interface SummarizerActions {
  // UI Actions
  setSidebarOpen: (open: boolean) => void;
  setTheme: (theme: 'light' | 'dark' | 'auto') => void;
  setLanguage: (language: string) => void;

  // Input Actions
  setUrlInput: (url: string) => void;
  setTextInput: (text: string) => void;
  setSelectedFile: (file: File | null) => void;

  // Validation Actions
  setUrlError: (error: string | null) => void;
  setFileError: (error: string | null) => void;
  setTextError: (error: string | null) => void;
  validateUrl: (url: string, pattern?: RegExp) => boolean;
  validateFile: (
    file: File,
    config?: { acceptedTypes?: string[]; maxSize?: number }
  ) => {
    isValid: boolean;
    error?: string;
  };

  // Summarizer Actions
  setCurrentType: (type: 'url' | 'file' | 'text') => void;
  setActiveTab: (tab: 'content' | 'repurpose' | 'chat' | 'transcript') => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSummary: (summary: Summary | null) => void;

  // Configuration Actions
  setCurrentConfig: (config: SummarizerConfig | null) => void;

  // Options Actions
  setSummaryType: (type: string) => void;
  setTone: (tone: string) => void;
  setFormat: (format: string) => void;
  setPromptType: (type: string) => void;
  setCustomPrompt: (prompt: string | null) => void;

  fetchQuota: () => Promise<void>;

  // CTA Dialog Actions
  setShowCTADialog: (show: boolean) => void;
  setCTADialogType: (type: string) => void;
  setCTAErrorMessage: (message: string | null) => void;
  setCTAFeatureContext: (context: string | null) => void;
  triggerCTADialog: (type: string, featureContext?: string, errorMessage?: string) => void;

  // Utility Actions
  clearError: () => void;
  clearAllInputs: () => void;
  clearResults: () => void;
  reset: () => void;
  setLimitOpen: (open: boolean) => void;

  // Repurpose Content Actions
  setIsRepurposing: (loading: boolean) => void;
  setRepurposeError: (error: string | null) => void;
  setSelectedLanguage: (language: string) => void;
  addPrompt: (prompt: Omit<PromptLibraryItem, 'id' | 'category' | 'icon'>) => void;
  removePrompt: (id: string) => void;
  updatePrompt: (prompt: Partial<PromptLibraryItem> & Pick<PromptLibraryItem, 'id'>) => void;
  setShowCustomPromptModal: (show: boolean) => void;
  setActivePrompt: (prompt: PromptLibraryItem | null) => void;

  // Prompt Library API Actions
  fetchPromptLibrary: () => Promise<void>;
  clearPromptLibraryCache: () => void;
  setPrompts: (prompts: PromptLibraryItem[]) => void;
  setCategories: (categories: string[]) => void;
  setIsLoadingPromptLibrary: (loading: boolean) => void;
  setPromptLibraryError: (error: string | null) => void;

  // Main Summarization Actions
  summarize: (data: SummarizeRequestData) => Promise<void>;
  uploadFile: (file: File) => Promise<void>;

  // Summary Cache Actions - Enhanced with language support
  getSummaryCacheKey: (detail_level: string, tone: string, language: string) => string;
  getCachedSummary: (detail_level: string, tone: string, language: string) => string | null;
  setCachedSummary: (
    content: string,
    detail_level: string,
    tone: string,
    language: string,
    custom_prompt?: string
  ) => void;
  clearSummaryCache: () => void;
  cleanupSummaryCache: () => void;

  // Translation Actions - Enhanced with detail_level and tone
  setTranslation: (
    language: string,
    detail_level: string,
    tone: string,
    translation: {
      summary: string | null;
      summary_as_md: string | null;
      summary_html: string | null;
      tldr?: string;
    }
  ) => void;
  getTranslation: (
    language: string,
    detail_level: string,
    tone: string
  ) =>
    | {
        summary: string | null;
        summary_as_md: string | null;
        summary_html: string | null;
        tldr?: string;
        detail_level: string;
        tone: string;
        language: string;
        timestamp: number;
      }
    | undefined;
  getTranslationKey: (language: string, detail_level: string, tone: string) => string;
  setIsTranslating: (isTranslating: boolean) => void;
  setTranslationError: (error: string | null) => void;
  markTranslationFailed: (language: string, detail_level: string, tone: string) => void;
  hasTranslationFailed: (language: string, detail_level: string, tone: string) => boolean;
  clearTranslations: () => void;
}

const initialState: SummarizerState = {
  // UI State
  sidebarOpen: false,
  theme: 'light',
  language: 'en-US',

  // Input State
  urlInput: '',
  textInput: '',
  selectedFile: null,

  // Validation State
  urlError: null,
  fileError: null,
  textError: null,

  // Summarizer State
  currentType: 'url',
  activeTab: 'content',
  isLoading: false,
  error: null,

  // Results
  currentSummary: null,
  // currentSummary: tempYoutubeSummary as unknown as Summary,
  summaryHistory: [],

  // Summary Cache
  summaryCache: {},

  // Configuration Management
  currentConfig: null,

  // Translation State
  translations: {},
  isTranslating: false,
  translationError: null,
  failedTranslations: new Set(),

  // File upload
  uploadProgress: 0,
  isUploading: false,
  uploadedUrl: '',
  uploadedFile: null,

  // API Configuration - Updated
  backendBaseUrl: 'https://app.tubeonai.com', // Legacy
  webAppBaseUrl: 'https://tubeonai-chat.duckdns.org', // Legacy
  // apiBaseUrl: 'http://localhost:3000', // New: Single base URL for new API
  apiBaseUrl: 'https://tubeonai-chat.duckdns.org', // New: Single base URL for new API

  // UI Options
  summaryType: 'Detailed',
  tone: 'Neutral',
  format: 'Paragraph',
  promptType: 'default',
  customPrompt: null,

  // Limit/Error State
  limitOpen: false,

  // Repurpose Content State
  isRepurposing: false,
  repurposeError: null,
  selectedLanguage: 'en',
  prompts: [],
  categories: [],
  isLoadingPromptLibrary: false,
  promptLibraryError: null,
  hasAttemptedPromptFetch: false,
  showCustomPromptModal: false,
  activePrompt: null,

  // Quota Management
  quota: null,
  quotaLoading: false,
  quotaError: null,

  // CTA Dialog State
  showCTADialog: false,
  ctaDialogType: 'quota_exceeded',
  ctaErrorMessage: null,
  ctaFeatureContext: null,
};

const getSourceTypeFromFile = (fileName: string): string => {
  const extension = fileName?.split('.').pop()?.toLowerCase() || '';

  if (['ppt', 'pptx'].includes(extension)) {
    return SOURCE_TYPES.PRESENTATION;
  } else if (['mp3'].includes(extension)) {
    return SOURCE_TYPES.AUDIO;
  } else if (['pdf'].includes(extension)) {
    return SOURCE_TYPES.PDF;
  } else if (['doc', 'docx', 'txt'].includes(extension)) {
    return SOURCE_TYPES.DOCUMENT;
  } else if (['mp4', 'webp'].includes(extension)) {
    return SOURCE_TYPES.VIDEO;
  }

  return SOURCE_TYPES.PDF; // default
};

// Helper function to map legacy source types to new tool names
const getToolNameFromSource = (source: string): SummarizeRequest['tool_name'] => {
  const mapping: Record<string, SummarizeRequest['tool_name']> = {
    [SOURCE_TYPES.URL]: 'web-page-summarizer',
    [SOURCE_TYPES.WEBPAGE]: 'web-page-summarizer',
    [SOURCE_TYPES.PDF]: 'pdf-summarizer',
    [SOURCE_TYPES.TEXT]: 'text-summarizer',
    [SOURCE_TYPES.AUDIO]: 'podcast-summarizer',
    [SOURCE_TYPES.VIDEO]: 'youtube-summarizer',
    [SOURCE_TYPES.PRESENTATION]: 'pdf-summarizer', // PowerPoint as PDF
    [SOURCE_TYPES.DOCUMENT]: 'pdf-summarizer',
  };
  return mapping[source] || 'web-page-summarizer';
};

export const useSummarizer = create<SummarizerState & SummarizerActions>()(
  devtools(
    immer((set, get) => {
      // Expose store state to window for API hooks
      if (typeof window !== 'undefined') {
        (window as any).__SUMMARIZER_STORE__ = get;
      }

      return {
        ...initialState,

        // UI Actions
        setSidebarOpen: (open: boolean) =>
          set((state) => {
            state.sidebarOpen = open;
          }),
        setTheme: (theme: 'light' | 'dark' | 'auto') =>
          set((state) => {
            state.theme = theme;
          }),
        setLanguage: (language: string) =>
          set((state) => {
            state.language = language;
          }),

        // Input Actions
        setUrlInput: (url: string) =>
          set((state) => {
            state.urlInput = url;
          }),
        setTextInput: (text: string) =>
          set((state) => {
            state.textInput = text;
          }),
        setSelectedFile: (file: File | null) =>
          set((state) => {
            state.selectedFile = file;
          }),

        // Validation Actions
        setUrlError: (error: string | null) =>
          set((state) => {
            state.urlError = error;
          }),
        setFileError: (error: string | null) =>
          set((state) => {
            state.fileError = error;
          }),
        setTextError: (error: string | null) =>
          set((state) => {
            state.textError = error;
          }),
        validateUrl: (url: string, pattern?: RegExp) => {
          // If a custom pattern is provided, use it
          if (pattern) {
            if (typeof pattern === 'string') {
              pattern = new RegExp(pattern);
            }
            return pattern.test(url);
          }

          try {
            // Try to construct a URL object - this validates according to URL standard
            new URL(url);
            return true;
          } catch (e) {
            // If no protocol is specified, try with https:// prefix
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
              try {
                new URL(`https://${url}`);
                return true;
              } catch (e) {
                return false;
              }
            }
            return false;
          }
        },
        validateFile: (file: File, config?: { acceptedTypes?: string[]; maxSize?: number }) => {
          if (!file) return { isValid: false, error: 'No file selected' };

          const defaultConfig = {
            acceptedTypes: ['.pdf', '.doc', '.docx', '.txt', '.mp3', '.mp4'],
            maxSize: 50 * 1024 * 1024, // 50MB
          };

          const fileConfig = { ...defaultConfig, ...config };

          // Check file type
          const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
          if (!fileConfig.acceptedTypes.includes(fileExtension)) {
            const supportedFormats = fileConfig.acceptedTypes.join(', ');
            return {
              isValid: false,
              error: `File type "${fileExtension}" is not supported. Please select a file with one of these formats: ${supportedFormats}`,
            };
          }

          // Check file size
          const maxSizeBytes = fileConfig.maxSize || 50 * 1024 * 1024;
          if (file.size > maxSizeBytes) {
            const formatSize = (bytes: number): string => {
              const k = 1024;
              const sizes = ['Bytes', 'KB', 'MB', 'GB'];
              const i = Math.floor(Math.log(bytes) / Math.log(k));
              return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
            };

            return {
              isValid: false,
              error: `File size (${formatSize(file.size)}) exceeds the maximum allowed size of ${formatSize(maxSizeBytes)}`,
            };
          }

          return { isValid: true, error: undefined };
        },

        // Summarizer Actions
        setCurrentType: (type: 'url' | 'file' | 'text') =>
          set((state) => {
            state.currentType = type;
          }),
        setActiveTab: (tab: 'content' | 'repurpose' | 'chat' | 'transcript') =>
          set((state) => {
            state.activeTab = tab;
          }),
        setLoading: (loading: boolean) =>
          set((state) => {
            state.isLoading = loading;
          }),
        setError: (error: string | null) =>
          set((state) => {
            state.error = error;
          }),
        setSummary: (summary: Summary | null) => {
          set((state) => {
            // Clear any previous errors and set summary
            state.error = null;
            state.currentSummary = summary;
          });
        },

        // Configuration Actions
        setCurrentConfig: (config: SummarizerConfig | null) => {
          set({ currentConfig: config });
        },
        // Options Actions
        setSummaryType: (type: string) =>
          set((state) => {
            state.summaryType = type;
          }),
        setTone: (tone: string) =>
          set((state) => {
            state.tone = tone;
          }),
        setFormat: (format: string) =>
          set((state) => {
            state.format = format;
          }),
        setPromptType: (type: string) =>
          set((state) => {
            state.promptType = type;
          }),
        setCustomPrompt: (prompt: string | null) =>
          set((state) => {
            state.customPrompt = prompt;
          }),

        // Quota Management Actions
        setQuota: (quota: UserQuotaData | null) =>
          set((state) => {
            state.quota = quota;
          }),
        setQuotaLoading: (loading: boolean) =>
          set((state) => {
            state.quotaLoading = loading;
          }),
        setQuotaError: (error: string | null) =>
          set((state) => {
            state.quotaError = error;
          }),
        fetchQuota: async () => {
          const { backendBaseUrl } = get();
          set((state) => {
            state.quotaLoading = true;
            state.quotaError = null;
          });

          try {
            const response = await fetch(`${backendBaseUrl}/api/public/quota`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'API-Version': 'v1',
                'X-User-IP': window.localStorage.getItem('user_ip') || '',
              },
            });

            const data = await response.json();
            if (data.success && data.data) {
              set((state) => {
                state.quota = data.data as UserQuotaData;
                state.quotaLoading = false;
              });
            } else {
              set((state) => {
                state.quotaError = data.message || 'Failed to fetch quota';
                state.quotaLoading = false;
              });
            }
          } catch (error) {
            set((state) => {
              state.quotaError = error instanceof Error ? error.message : 'Failed to fetch quota';
              state.quotaLoading = false;
            });
          }
        },
        // CTA Dialog Actions
        setShowCTADialog: (show: boolean) =>
          set((state) => {
            state.showCTADialog = show;
          }),
        setCTADialogType: (type: string) =>
          set((state) => {
            state.ctaDialogType = type;
          }),
        setCTAErrorMessage: (message: string | null) =>
          set((state) => {
            state.ctaErrorMessage = message;
          }),
        setCTAFeatureContext: (context: string | null) =>
          set((state) => {
            state.ctaFeatureContext = context;
          }),
        triggerCTADialog: (type: string, featureContext?: string, errorMessage?: string) =>
          set((state) => {
            state.ctaDialogType = type;
            state.ctaFeatureContext = featureContext || null;
            state.ctaErrorMessage = errorMessage || null;
            state.showCTADialog = true;
          }),

        // Utility Actions
        clearError: () =>
          set((state) => {
            state.error = null;
          }),
        clearAllInputs: () =>
          set((state) => {
            state.urlInput = '';
            state.textInput = '';
            state.selectedFile = null;
            state.uploadedFile = null;
            state.uploadedUrl = '';
            state.urlError = null;
            state.fileError = null;
            state.textError = null;
          }),
        clearResults: () =>
          set((state) => {
            state.currentSummary = null;
            state.currentConfig = null;
            state.summaryCache = {}; // Clear cache when loading new content
          }),
        reset: () =>
          set((state) => {
            const currentConfig = state.currentConfig;
            Object.assign(state, { ...initialState, currentConfig });
          }),
        setLimitOpen: (open: boolean) =>
          set((state) => {
            state.limitOpen = open;
          }),

        // Repurpose Content Actions
        setIsRepurposing: (loading: boolean) =>
          set((state) => {
            state.isRepurposing = loading;
          }),
        setRepurposeError: (error: string | null) =>
          set((state) => {
            state.repurposeError = error;
          }),
        setSelectedLanguage: (language: string) =>
          set((state) => {
            state.selectedLanguage = language;
          }),
        addPrompt: (prompt: Omit<PromptLibraryItem, 'id' | 'category' | 'icon'>) =>
          set((state) => {
            const newPrompt: PromptLibraryItem = {
              ...prompt,
              id: `custom_${Date.now()}`,
              category: 'custom',
              icon: '✨',
            };
            state.prompts.push(newPrompt);
          }),
        removePrompt: (id: string) =>
          set((state) => {
            state.prompts = state.prompts.filter((p) => p.id !== id);
          }),
        updatePrompt: (prompt: Partial<PromptLibraryItem> & Pick<PromptLibraryItem, 'id'>) =>
          set((state) => {
            const index = state.prompts.findIndex((p) => p.id === prompt.id);
            if (index !== -1) {
              const merged = { ...state.prompts[index], ...prompt } as PromptLibraryItem;
              state.prompts[index] = merged;
              if (state.activePrompt && state.activePrompt.id === prompt.id) {
                state.activePrompt = merged;
              }
            }
          }),
        setShowCustomPromptModal: (show: boolean) =>
          set((state) => {
            state.showCustomPromptModal = show;
          }),
        setActivePrompt: (prompt: PromptLibraryItem | null) =>
          set((state) => {
            state.activePrompt = prompt;
          }),

        // Prompt Library API Actions
        fetchPromptLibrary: async () => {
          const state = get();
          const CACHE_KEY = 'tubeonai_prompt_library_cache';
          const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
          const CACHE_VERSION = '1.0';

          // Helper function to check if cache is expired
          const isCacheExpired = (timestamp: number): boolean => {
            return Date.now() - timestamp > CACHE_DURATION;
          };

          // Helper function to get cache from localStorage
          const getPromptLibraryCache = () => {
            try {
              const cached = localStorage.getItem(CACHE_KEY);
              if (!cached) return null;

              const parsedCache = JSON.parse(cached);

              // Check version and expiry
              if (
                parsedCache.version !== CACHE_VERSION ||
                !parsedCache.timestamp ||
                isCacheExpired(parsedCache.timestamp)
              ) {
                localStorage.removeItem(CACHE_KEY);
                return null;
              }

              return parsedCache.data;
            } catch (error) {
              // If cache is corrupted, remove it
              localStorage.removeItem(CACHE_KEY);
              return null;
            }
          };

          // Helper function to set cache in localStorage
          const setPromptLibraryCache = (data: {
            prompts: PromptLibraryItem[];
            categories: string[];
          }) => {
            try {
              const cacheData = {
                version: CACHE_VERSION,
                timestamp: Date.now(),
                data: data,
              };
              localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
            } catch (error) {
              // Silently fail if localStorage is full or unavailable
              console.warn('Failed to cache prompt library:', error);
            }
          };

          // Prevent duplicate fetches
          if (
            state.isLoadingPromptLibrary ||
            (state.hasAttemptedPromptFetch && state.prompts.length > 0)
          ) {
            return;
          }

          // Check localStorage cache first
          const cachedData = getPromptLibraryCache();
          if (cachedData && cachedData.prompts && cachedData.categories) {
            set((draft) => {
              draft.prompts = cachedData.prompts;
              draft.categories = cachedData.categories;
              draft.hasAttemptedPromptFetch = true;
              draft.isLoadingPromptLibrary = false;
              draft.promptLibraryError = null;
            });
            return; // Exit early with cached data
          }

          // If no cache, fetch from API
          set((draft) => {
            draft.isLoadingPromptLibrary = true;
            draft.promptLibraryError = null;
            draft.hasAttemptedPromptFetch = true;
          });

          try {
            const response = await fetch(`${state.apiBaseUrl}/api/public/prompt-library`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: PromptLibraryResponse = await response.json();

            // Update state
            set((draft) => {
              draft.prompts = data.prompts || [];
              draft.categories = data.categories || [];
              draft.isLoadingPromptLibrary = false;
              draft.promptLibraryError = null;
            });

            // Cache the successful response
            setPromptLibraryCache({
              prompts: data.prompts || [],
              categories: data.categories || [],
            });
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : 'Failed to fetch prompt library';
            set((draft) => {
              draft.promptLibraryError = errorMessage;
              draft.isLoadingPromptLibrary = false;
            });
          }
        },

        clearPromptLibraryCache: () => {
          const CACHE_KEY = 'tubeonai_prompt_library_cache';
          try {
            localStorage.removeItem(CACHE_KEY);
            // Reset the hasAttemptedPromptFetch flag to allow fetching again
            set((state) => {
              state.hasAttemptedPromptFetch = false;
            });
          } catch (error) {
            console.warn('Failed to clear prompt library cache:', error);
          }
        },

        setPrompts: (prompts: PromptLibraryItem[]) =>
          set((state) => {
            state.prompts = prompts;
          }),

        setCategories: (categories: string[]) =>
          set((state) => {
            state.categories = categories;
          }),

        setIsLoadingPromptLibrary: (loading: boolean) =>
          set((state) => {
            state.isLoadingPromptLibrary = loading;
          }),

        setPromptLibraryError: (error: string | null) =>
          set((state) => {
            state.promptLibraryError = error;
          }),

        // Main Summarization Actions
        summarize: async (data: SummarizeRequestData) => {
          const state = get();

          set((draft) => {
            draft.isLoading = true;
            draft.error = null;
          });

          try {
            // Determine tool name based on source
            const toolName = getToolNameFromSource(data.source);

            // Create summarize request
            const request: SummarizeRequest = {
              tool_name: toolName,
              url: data.link_or_id,
              text_content: data.text,
              detail_level: data.summaryType || state.summaryType || 'Detailed',
              tone: data.tone || state.tone || 'Neutral',
              language: data.language || state.language || 'en-US',
              user_id: state.quota?.user_id,
              link_or_id: data.link_or_id,
            };

            const response = await fetch(`${state.apiBaseUrl}/api/public/summarize`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify(request),
            });

            const result = (await response.json()) as APIResponse<Summary>;

            if (result.success && result.data) {
              // Validate transcript requirement after successful API response
              const hasTranscript = Boolean(
                result.data.transcript && result.data.transcript.trim().length > 0
              );

              if (!hasTranscript) {
                const errorMessage =
                  'This content cannot be displayed because the source transcript is missing or incomplete. Please try again with a different source.';

                set((draft) => {
                  draft.error = errorMessage;
                  draft.currentSummary = null;
                  draft.isLoading = false;
                });

                return; // Early return, don't continue processing
              }

              set((draft) => {
                draft.currentSummary = result.data || null;
                draft.isLoading = false;

                draft.urlError = null;
                draft.fileError = null;
                draft.textError = null;

                draft.uploadProgress = 0;
                draft.isUploading = false;
                draft.uploadedUrl = '';
                draft.uploadedFile = null;

                // Clear any previous translations and caches related to prior content
                draft.isTranslating = false;
                draft.translationError = null;
                draft.translations = {};
                draft.summaryCache = {};
                draft.failedTranslations = new Set();
              });
            } else {
              // Check if error indicates sign-up/upgrade required
              const errorMessage =
                result.message?.replace('(and 1 more error)', '') || 'Failed to summarize';

              // Determine CTA type based on error
              setTimeout(() => {
                if (
                  errorMessage.includes('quota') ||
                  errorMessage.includes('limit') ||
                  errorMessage.includes('exceeded') ||
                  errorMessage.includes('enough')
                ) {
                  state.triggerCTADialog('quota_exceeded', 'summary', errorMessage);
                } else if (errorMessage.includes('premium') || errorMessage.includes('upgrade')) {
                  state.triggerCTADialog('premium_features', 'premium', errorMessage);
                } else if (
                  errorMessage.includes('sign up') ||
                  errorMessage.includes('authentication') ||
                  errorMessage.includes('login')
                ) {
                  state.triggerCTADialog('sign_up_required', 'summary', errorMessage);
                } else {
                  // For other errors, still show CTA but with default type
                  state.triggerCTADialog('quota_exceeded', 'summary', errorMessage);
                }
              }, 3000);

              throw new Error(errorMessage);
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'An error occurred';

            set((draft) => {
              draft.error = errorMessage;
              draft.isLoading = false;
            });

            // If error message doesn't contain specific trigger words, it means the CTA wasn't triggered in the response handling
            // This handles network errors, timeout errors, etc.
            if (
              !errorMessage.includes('quota') &&
              !errorMessage.includes('premium') &&
              !errorMessage.includes('sign up')
            ) {
              // For network/generic errors, show sign-up CTA as it might help with API access
              get().triggerCTADialog('sign_up_required', 'summary', errorMessage);
            }
          }
        },

        uploadFile: async (file: File) => {
          set((draft) => {
            draft.isUploading = true;
            draft.uploadProgress = 0;
            draft.fileError = null;
          });

          try {
            const state = get();

            // Get presigned URL for file upload
            const { data } = await axios.post(
              `${state.backendBaseUrl}/api/get-presigned-url-for-free-users`,
              {
                file_name: file.name,
                file_type: file.type,
              }
            );

            if (!data.url?.url) {
              throw new Error('Failed to get upload URL');
            }

            // Upload file to S3 using presigned URL
            await axios({
              url: data.url.url,
              method: 'put',
              data: file,
              headers: { 'Content-Type': file.type },
              timeout: 10 * 60 * 1000, // 10 minutes timeout
              onUploadProgress: (progress) => {
                const progressPercent = Math.round((progress.loaded / (progress.total || 1)) * 100);
                set((draft) => {
                  draft.uploadProgress = progressPercent;
                });
              },
            });

            // Extract the actual URL without query parameters
            const actualUrl = data.url.url.split('?')[0];

            // Update state with successful upload
            set((draft) => {
              draft.selectedFile = file;
              draft.uploadedFile = file; // Save the file for preview
              draft.uploadProgress = 100;
              draft.isUploading = false;
              draft.uploadedUrl = actualUrl;
            });
          } catch (error) {
            set((draft) => {
              draft.fileError = error instanceof Error ? error.message : 'Upload failed';
              draft.isUploading = false;
              draft.uploadProgress = 0;
            });
          }
        },

        // Translation Actions - Enhanced with detail_level and tone
        getTranslationKey: (language: string, detail_level: string, tone: string) => {
          const normalizedLanguage = language.toLowerCase().replace('-', '_');
          const normalizedDetailLevel = detail_level.toLowerCase();
          const normalizedTone = tone.toLowerCase();
          return `${normalizedLanguage}_${normalizedDetailLevel}_${normalizedTone}`;
        },

        setTranslation: (language: string, detail_level: string, tone: string, translation) => {
          const state = get();
          const translationKey = state.getTranslationKey(language, detail_level, tone);

          set((draft) => {
            draft.translations[translationKey] = {
              ...translation,
              detail_level: detail_level.toLowerCase(),
              tone: tone.toLowerCase(),
              language: language.toLowerCase(),
              timestamp: Date.now(),
            };
          });
        },

        getTranslation: (language: string, detail_level: string, tone: string) => {
          const state = get();

          // Handle both 'en' and 'en-US' as English
          if (language === 'en' || language === 'en-US') {
            // Return the original summary for English with metadata
            if (state.currentSummary) {
              return {
                summary: state.currentSummary.summary || null,
                summary_as_md:
                  state.currentSummary.summary_as_md || state.currentSummary.summary || null,
                summary_html: state.currentSummary.summary || null,
                detail_level: detail_level.toLowerCase(),
                tone: tone.toLowerCase(),
                language: 'en',
                timestamp: Date.now(),
              };
            }
          }

          const translationKey = state.getTranslationKey(language, detail_level, tone);
          return state.translations[translationKey];
        },

        setIsTranslating: (isTranslating: boolean) =>
          set((draft) => {
            draft.isTranslating = isTranslating;
          }),

        setTranslationError: (error: string | null) =>
          set((draft) => {
            draft.translationError = error;
          }),

        markTranslationFailed: (language: string, detail_level: string, tone: string) =>
          set((draft) => {
            const key = `${language.toLowerCase()}_${detail_level.toLowerCase()}_${tone.toLowerCase()}`;
            const newFailed = new Set(draft.failedTranslations);
            newFailed.add(key);
            draft.failedTranslations = newFailed;
          }),

        hasTranslationFailed: (language: string, detail_level: string, tone: string) => {
          const state = get();
          const key = `${language.toLowerCase()}_${detail_level.toLowerCase()}_${tone.toLowerCase()}`;
          return state.failedTranslations.has(key);
        },

        clearTranslations: () =>
          set((draft) => {
            draft.translations = {};
            draft.translationError = null;
            draft.failedTranslations = new Set();
          }),

        // Summary Cache Actions - Enhanced with language support
        getSummaryCacheKey: (detail_level: string, tone: string, language: string) => {
          const normalizedDetailLevel = detail_level.toLowerCase();
          const normalizedTone = tone.toLowerCase();
          const normalizedLanguage = language.toLowerCase().replace('-', '_');
          return `${normalizedLanguage}_${normalizedDetailLevel}_${normalizedTone}`;
        },

        getCachedSummary: (detail_level: string, tone: string, language: string) => {
          const state = get();
          const cacheKey = state.getSummaryCacheKey(detail_level, tone, language);
          const cachedEntry = state.summaryCache[cacheKey];

          if (cachedEntry) {
            // Check if cache entry is not too old (24 hours)
            const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
            const isExpired = Date.now() - cachedEntry.timestamp > maxAge;

            if (!isExpired) {
              return cachedEntry.content;
            } else {
              // Remove expired entry
              set((draft) => {
                delete draft.summaryCache[cacheKey];
              });
            }
          }

          return null;
        },

        setCachedSummary: (
          content: string,
          detail_level: string,
          tone: string,
          language: string,
          custom_prompt?: string
        ) => {
          const state = get();
          const cacheKey = state.getSummaryCacheKey(detail_level, tone, language);

          set((draft) => {
            draft.summaryCache[cacheKey] = {
              content,
              timestamp: Date.now(),
              detail_level: detail_level.toLowerCase(),
              tone: tone.toLowerCase(),
              language: language.toLowerCase(),
              custom_prompt,
            };
          });
        },

        clearSummaryCache: () =>
          set((draft) => {
            draft.summaryCache = {};
          }),

        cleanupSummaryCache: () => {
          const state = get();
          const maxEntries = 10; // Keep max 10 cached variants per summary
          const cacheEntries = Object.entries(state.summaryCache);

          if (cacheEntries.length > maxEntries) {
            // Sort by timestamp (oldest first) and remove oldest entries
            const sortedEntries = cacheEntries.sort(([, a], [, b]) => a.timestamp - b.timestamp);
            const entriesToKeep = sortedEntries.slice(-maxEntries);

            set((draft) => {
              draft.summaryCache = Object.fromEntries(entriesToKeep);
            });
          }
        },
      };
    }),
    {
      name: 'summarizer-storage',
    }
  )
);

// Export helper function for use in components
export { getSourceTypeFromFile };
